/* eslint-disable max-len */
import {
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { message } from 'antd';

import { useClickOutside } from 'app/hooks';
import type { Folder } from 'app/hooks/useFolders';
import {
  shareFolder,
  unshareFolder,
  getFolderCollaborators,
  syncFolderTourCollaborators,
  type FolderCollaboratorInfo,
} from 'app/services/firebase/functions';

interface Props {
  folder: Folder;
  /** Number of tours in the folder, or null while the tours list is still
   *  loading for the first time (suppresses the count to avoid showing a
   *  bogus "0 tours" before tours arrive over the network). */
  tourCount: number | null;
  /** True when the current user owns this folder. Mutations are gated on it. */
  ownedByMe: boolean;
  onOpen: (folderId: string) => void;
  onRename: (folderId: string, nextName: string) => void;
  onDelete: (folderId: string) => void;
}

const FolderIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="text-black/60"
  >
    <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h3.17c.66 0 1.29.26 1.77.73l1.06 1.06c.47.47 1.1.73 1.77.73H18.5A2.5 2.5 0 0 1 21 9v8.5A2.5 2.5 0 0 1 18.5 20h-13A2.5 2.5 0 0 1 3 17.5V6.75c0-.14.01-.17 1-.25z" />
  </svg>
);

const KebabIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
    <circle cx="4" cy="10" r="1.6" />
    <circle cx="10" cy="10" r="1.6" />
    <circle cx="16" cy="10" r="1.6" />
  </svg>
);

const SharedIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="6" cy="5.5" r="2.2" />
    <circle cx="11" cy="6.5" r="1.8" />
    <path d="M2 13c.5-2 2-3 4-3s3.5 1 4 3" />
    <path d="M9 11.5c.4-1 1.5-1.5 2.5-1.5s2 .5 2.5 1.5" />
  </svg>
);

const FolderCardImpl = ({
  folder,
  tourCount,
  ownedByMe,
  onOpen,
  onRename,
  onDelete,
}: Props): ReactElement => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(folder.name);
  const menuRef = useRef<HTMLDivElement>(null);

  // Sharing modal state
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCollaborators, setShareCollaborators] = useState<
    FolderCollaboratorInfo[]
  >([]);
  const [shareEmail, setShareEmail] = useState('');
  const [shareAdding, setShareAdding] = useState(false);
  const shareModalRef = useRef<HTMLDivElement>(null);

  useClickOutside(menuRef, (isOutside) => {
    if (isOutside) setMenuOpen(false);
  });

  useClickOutside(shareModalRef, (isOutside) => {
    if (isOutside && shareOpen) setShareOpen(false);
  });

  const fetchShares = useCallback(async () => {
    try {
      setShareLoading(true);
      const res = await getFolderCollaborators(folder.id, folder.ownerUid);
      setShareCollaborators(res.collaborators);
    } catch (err) {
      console.warn('[FolderCard] sharing list failed', err);
    } finally {
      setShareLoading(false);
    }
  }, [folder.id, folder.ownerUid]);

  useEffect(() => {
    if (shareOpen) fetchShares();
  }, [shareOpen, fetchShares]);

  const handleActivate = () => {
    if (menuOpen || renaming || shareOpen) return;
    onOpen(folder.id);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (renaming || menuOpen || shareOpen) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(folder.id);
    }
  };

  const commitRename = () => {
    const next = renameValue.trim();
    if (next && next !== folder.name) onRename(folder.id, next);
    setRenaming(false);
  };

  const handleAddShare = async () => {
    const trimmed = shareEmail.trim();
    if (!trimmed) return;
    try {
      setShareAdding(true);
      const res = await shareFolder(folder.id, trimmed, folder.ownerUid);
      setShareCollaborators((prev) => [...prev, res.collaborator]);
      setShareEmail('');
      message.success(`${res.collaborator.email} added`);
      // Cascade: grant the new collaborator access to every tour currently
      // in the folder so they actually see the contents on their dashboard.
      // Fire-and-forget — if the cascade fails the share itself is intact;
      // owner can re-trigger via re-share or per-tour collaborator add.
      syncFolderTourCollaborators(folder.id, folder.ownerUid).catch((err) => {
        console.warn('[FolderCard] cascade after share failed', err);
      });
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to add collaborator';
      message.error(msg);
    } finally {
      setShareAdding(false);
    }
  };

  const handleRemoveShare = async (uid: string) => {
    try {
      await unshareFolder(folder.id, uid, folder.ownerUid);
      setShareCollaborators((prev) => prev.filter((c) => c.uid !== uid));
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to remove collaborator';
      message.error(msg);
    }
  };

  const isShared =
    folder.collaboratorUids && folder.collaboratorUids.length > 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={handleKey}
      className="group relative bg-white border border-black/10 rounded-[18px] shadow-[0_0_10px_rgba(0,0,0,0.06)] w-full flex items-center gap-5 px-5 py-4 cursor-pointer transition-all hover:shadow-[0_0_20px_rgba(0,0,0,0.10)] hover:-translate-y-px hover:z-30 focus-within:z-30 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-black/20"
    >
      <div className="relative w-20 h-20 rounded-xl shrink-0 bg-black/5 flex items-center justify-center">
        <FolderIcon />
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        {renaming ? (
          <input
            autoFocus
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitRename();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setRenameValue(folder.name);
                setRenaming(false);
              }
            }}
            onBlur={commitRename}
            className="text-[15px] font-semibold text-[#151A30] bg-transparent border-b border-black/20 outline-none px-0 py-0.5"
          />
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-[15px] font-semibold text-[#151A30] truncate m-0">
              {folder.name}
            </h3>
            {(isShared || !ownedByMe) && (
              <span
                className={classNames(
                  'shrink-0 inline-flex items-center gap-1 px-1.5 h-5 rounded-full text-[10px] font-semibold uppercase tracking-wide',
                  ownedByMe
                    ? 'bg-black/[0.06] text-black/60'
                    : 'bg-black/85 text-white',
                )}
                title={ownedByMe ? 'Shared with others' : 'Shared with you'}
              >
                <SharedIcon />
                {ownedByMe ? 'Shared' : 'Shared with you'}
              </span>
            )}
          </div>
        )}
        {tourCount === null ? (
          <div className="h-3 w-16 rounded bg-black/5 mt-1 animate-pulse" />
        ) : (
          <p className="text-[13px] text-black/50 truncate m-0 mt-0.5">
            {tourCount} {tourCount === 1 ? 'tour' : 'tours'}
          </p>
        )}
      </div>

      <div ref={menuRef} className="relative shrink-0">
        <button
          type="button"
          aria-label="Folder actions"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((o) => !o);
          }}
          className={classNames(
            'w-8 h-8 flex items-center justify-center bg-transparent border-0',
            'text-black/55 hover:text-black/85 focus:outline-none',
            'transition-opacity md:opacity-0 md:group-hover:opacity-100',
            menuOpen && 'md:opacity-100 text-black/85',
          )}
        >
          <KebabIcon />
        </button>
        {menuOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="glass-popover absolute top-full right-0 mt-2 min-w-[180px] p-1 z-20"
          >
            {ownedByMe ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setShareOpen(true);
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-[13px] font-medium text-black/75 hover:bg-black/[0.04] hover:text-black/95 transition-colors bg-transparent border-0 focus:outline-none"
                >
                  Manage sharing…
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setRenameValue(folder.name);
                    setRenaming(true);
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-[13px] font-medium text-black/75 hover:bg-black/[0.04] hover:text-black/95 transition-colors bg-transparent border-0 focus:outline-none"
                >
                  Rename
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    if (
                      window.confirm(
                        `Delete "${folder.name}"? Tours inside will move back to All Tours.`,
                      )
                    ) {
                      onDelete(folder.id);
                    }
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-[13px] font-medium text-red-600 hover:bg-red-500/[0.06] transition-colors bg-transparent border-0 focus:outline-none"
                >
                  Delete folder
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  onOpen(folder.id);
                }}
                className="block w-full text-left px-3 py-2 rounded-md text-[13px] font-medium text-black/75 hover:bg-black/[0.04] hover:text-black/95 transition-colors bg-transparent border-0 focus:outline-none"
              >
                Open folder
              </button>
            )}
          </div>
        )}
      </div>

      {shareOpen &&
        createPortal(
          <div
            onClick={(e) => {
              e.stopPropagation();
              setShareOpen(false);
            }}
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
          >
            <div
              ref={shareModalRef}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-[18px] shadow-[0_8px_32px_rgba(0,0,0,0.18)] w-full max-w-md max-h-[80vh] overflow-y-auto"
            >
              <header className="relative px-6 py-4 border-b border-black/[0.08]">
                <h3 className="text-[15px] font-semibold text-black/85 m-0 pr-8">
                  Share &ldquo;{folder.name}&rdquo;
                </h3>
                <p className="text-[12px] text-black/55 mt-1 m-0 pr-8">
                  Collaborators can see this folder and the tours inside it that
                  they already have access to.
                </p>
                <button
                  type="button"
                  onClick={() => setShareOpen(false)}
                  aria-label="Close"
                  className="absolute top-3 right-3 p-0 bg-transparent border-none text-black/50 hover:text-black/85 transition-colors cursor-pointer"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4L12 12M12 4L4 12" />
                  </svg>
                </button>
              </header>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-5">
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddShare()}
                    placeholder="Enter email address"
                    disabled={shareAdding}
                    className="flex-1 min-w-0 h-10 px-3 bg-white border border-black/10 rounded-lg text-[14px] text-black/85 placeholder:text-black/40 outline-none focus:border-black/40 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={handleAddShare}
                    disabled={shareAdding || !shareEmail.trim()}
                    className="btn-primary shrink-0"
                  >
                    {shareAdding ? 'Adding…' : 'Add'}
                  </button>
                </div>

                {shareLoading ? (
                  <p className="text-[13px] text-black/50 m-0 py-3 text-center">
                    Loading…
                  </p>
                ) : shareCollaborators.length === 0 ? (
                  <p className="text-[13px] text-black/50 m-0 py-4 text-center">
                    Nobody else has access yet.
                  </p>
                ) : (
                  <div className="flex flex-col gap-3">
                    {shareCollaborators.map((c) => (
                      <div key={c.uid} className="flex items-center gap-3 py-1">
                        <div className="w-9 h-9 rounded-full bg-black/85 text-white flex items-center justify-center text-[11px] font-semibold shrink-0 shadow-sm">
                          {(
                            c.displayName?.[0] ||
                            c.email?.[0] ||
                            '?'
                          ).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          {c.displayName && (
                            <p className="text-[13px] font-medium text-black/85 m-0 truncate">
                              {c.displayName}
                            </p>
                          )}
                          <p className="text-[12px] text-black/50 m-0 truncate">
                            {c.email || c.uid}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveShare(c.uid)}
                          className="btn-toolbar text-black/40 hover:text-red-500"
                          aria-label={`Remove ${c.email}`}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M4 4L12 12M12 4L4 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export const FolderCard = memo(FolderCardImpl);
